import { createApp } from '@backstage/frontend-defaults';
import {
  OpenIdConnectApi,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
  createApiRef,
  createFrontendModule,
  configApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
  ApiBlueprint,
  useApi,
} from '@backstage/frontend-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { SignInPage } from '@backstage/core-components';
import type { IdentityProviders } from '@backstage/core-components';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import catalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';
import kubernetesPlugin from '@backstage/plugin-kubernetes/alpha';
import { navModule } from './modules/nav';
import { gasCityPlugin } from './plugins/gas-city';
import { deliveryPlugin } from './plugins/delivery/plugin';
import { homePlugin } from './plugins/home';
import { repositoriesPlugin } from './plugins/repositories';

const oidcAuthApiRef = createApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>().with({ id: 'auth.oidc' });

const oidcAuthApi = ApiBlueprint.make({
  name: 'oidc',
  params: defineParams =>
    defineParams({
      api: oidcAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          environment: configApi.getOptionalString('auth.environment'),
          // `id: 'oidc'` must match the backend provider strategy id.
          provider: { id: 'oidc', title: 'Cedarwell', icon: () => null },
          defaultScopes: ['openid', 'profile', 'email'],
        }),
    }),
});

export function getSignInProvidersForEnvironment(
  authEnvironment?: string,
): IdentityProviders {
  if (authEnvironment === 'production') {
    return [
      {
        id: 'cedarwell-oidc',
        title: 'Cedarwell',
        message: 'Sign in with Cedarwell',
        apiRef: oidcAuthApiRef,
      },
    ];
  }

  return ['guest'];
}

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => {
      const CedarwellSignInPage = (props: any) => {
        const configApi = useApi(configApiRef);
        return (
          <SignInPage
            {...props}
            providers={getSignInProvidersForEnvironment(
              configApi.getOptionalString('auth.environment'),
            )}
          />
        );
      };

      return CedarwellSignInPage;
    },
  },
});

export default createApp({
  features: [
    homePlugin,
    catalogPlugin,
    catalogGraphPlugin,
    kubernetesPlugin,
    gasCityPlugin,
    deliveryPlugin,
    repositoriesPlugin,
    navModule,
    createFrontendModule({
      pluginId: 'app',
      extensions: [oidcAuthApi, signInPage],
    }),
  ],
});
